import React, {PureComponent} from 'react';

import {InvoicesSearchBar} from 'components/Invoices/InvoicesSearchBar';
import {IPageResponse, SortDirection} from "../common/interfaces/IPageResponse";
import {userService} from "../services/user.service";
import {AuthenticationContext} from "../services/context/authentication.context";
import { DateRangeInput } from 'components/DateRangePicker/DateRangePicker';
import DocumentService from 'services/document.service';
import { IFuelInvoiceSortFields, FuelInvoiceFilterType, INVOICE_TYPE_FILTER_OPTIONS, IFuelInvoice, FuelInvoiceType, DOCUMENT_TYPES_MULTI, DOCUMENT_TYPES_AGGREGATE, IFuelInvoiceRequest } from 'models/fuel-invoice';
import { AxiosResponse } from 'axios';
import { getParams } from 'helpers';
import { PageProps } from 'models/page';
import { FuelInvoices } from 'components/Invoices/FuelInvoices/FuelInvoices';
import { IElectricityInvoice, IElectricityInvoiceRequest, IElectricityInvoiceSortFields } from 'models/electricity/electricity-invoice';
import ElectricityInvoiceService from 'services/electricity/electricity-invoice.service';
import { ElectricityInvoicesTable } from 'components/Invoices/Electricity/ElectricityInvoicesTable';
import PaymentService from 'services/payment.service';
import { BankType, DocumentPayment, PaymentLocale, PaymentMethodType } from 'models/payment';

export enum ActiveInvoiceType {
    FUEL,
    ELECTRICITY
}

interface IInvoicesState {
  customerId: number | null;
  contactId: number | null;
  fuel: {
    filters: { [key in FuelInvoiceFilterType]?: any };
    isRequesting: boolean;
    invoices: IPageResponse<IFuelInvoice> | null;
    unpaidInvoices: IPageResponse<IFuelInvoice> | null;
    paymentModalVisible: boolean;
    paymentStatus: string | null;
    pageRequest: IFuelInvoiceRequest;
  };
  electricity : {
    isRequesting: boolean;
    invoices: IPageResponse<IElectricityInvoice> | null;
    pageRequest: IElectricityInvoiceRequest;
  }
  activeInvoiceType: ActiveInvoiceType
}

const INITIAL_FUEL_INVOICES_STATE = {
    paymentModalVisible: false,
    paymentStatus: null,
    invoices: null,
    unpaidInvoices: null,
    isRequesting: false,
    pageRequest: {
        page: 0,
        size: 20,
        sortDirection: SortDirection.ASC,
        sortField: IFuelInvoiceSortFields.DOCUMENT_DATE_CREATED,
        types:  DOCUMENT_TYPES_MULTI + ',' + DOCUMENT_TYPES_AGGREGATE
    },
    filters: {
        [FuelInvoiceFilterType.TYPE]: INVOICE_TYPE_FILTER_OPTIONS[0],
        [FuelInvoiceFilterType.DATE]: null
    }
}

const INITIAL_ELECTRICITY_INVOICES_STATE = {
    invoices: null,
    isRequesting: false,
    pageRequest: {
        page: 0,
        size: 20,
        sortDirection: SortDirection.ASC,
        sortField: IElectricityInvoiceSortFields.ID
    },
}

export class Invoices extends PureComponent<PageProps, IInvoicesState> {

    static contextType = AuthenticationContext;
    constructor(props: PageProps) {
        super(props);
        this.state = {
            fuel: INITIAL_FUEL_INVOICES_STATE,
            electricity : INITIAL_ELECTRICITY_INVOICES_STATE,
            customerId: null,
            contactId: null,
            activeInvoiceType: ActiveInvoiceType.FUEL
        };
        this.props.onLoadValidatePage();
    }  

    getFuelInvoices = () => {
        if (this.state.customerId === null)
            return;
        this.setState({ 
            ...this.state,
            fuel: {
                ...this.state.fuel,
                isRequesting: true
            }
         });
        DocumentService.getInvoices(this.state.customerId, this.state.fuel.pageRequest).then((response: IPageResponse<IFuelInvoice>) => {
            this.setState({
                ...this.state,
                fuel: {
                    ...this.state.fuel,
                    invoices: response,
                    isRequesting: false,
                }
            });
        });
    };

    getElectricityInvoices = () => {
        if (this.state.customerId === null)
            return;
        this.setState({ 
            ...this.state,
            electricity: {
                ...this.state.electricity,
                isRequesting: true
            }
         });

         ElectricityInvoiceService.getInvoices(this.state.customerId, this.state.electricity.pageRequest)
            .then((response: IPageResponse<IElectricityInvoice>) => {
                this.setState({
                    ...this.state,
                    electricity: {
                        ...this.state.electricity,
                        invoices: response,
                        isRequesting: false
                    }
                });
        });
    };

    getUnpaidFuelInvoices = () => {
        if (this.state.customerId === null)
            return;
        
        DocumentService.getInvoices(this.state.customerId, {
            onlyNotPaid: true,
            types: DOCUMENT_TYPES_MULTI + ',' + DOCUMENT_TYPES_AGGREGATE,
            page: 0,
            size: 1000
        }).then((response: IPageResponse<IFuelInvoice>) => {
            this.setState({
                ...this.state,
                fuel: {
                    ...this.state.fuel,
                    unpaidInvoices: response
                }
            });
        });
    }

    searchFuelInvoices = (request: any) => {
        DocumentService.getInvoices(userService.getCurrentCustomer().customerId, request).then((response: IPageResponse<IFuelInvoice>) => {
            this.setState({
                ...this.state,
                fuel :{
                    ...this.state.fuel,
                    invoices: response
                }
            });
        });
    };
    
    beginFuelInvoicePayment = (paymentMethodType: PaymentMethodType, documentPayments: DocumentPayment[], bankType?: BankType, ) => {
        if (this.state.customerId === null || this.state.contactId === null) return Promise.resolve();
        
        return PaymentService.beginPayment({
            locale: PaymentLocale.ET,
            bankType,
            customerId: this.state.customerId,
            contactPersonId: this.state.contactId,
            documentPayments,
            paymentMethodType
        }).then((response: AxiosResponse) => {
            window.location.href = response.data;
        });
    }

    onFuelInvoicePageChange = (targetPage: number) => {
        if (this.state.fuel.pageRequest.page !== targetPage - 1) {
            this.setState({
                ...this.state,
                fuel: {
                    ...this.state.fuel,
                    pageRequest: {
                        ...this.state.fuel.pageRequest,
                        page: targetPage - 1,
                    }
                }
            }, () => {
                this.getFuelInvoices();
            });
        }
    };

    onElectricityInvoicePageChange = (targetPage: number) => {
        if (this.state.electricity.pageRequest.page !== targetPage - 1) {
            this.setState({
                ...this.state,
                electricity: {
                    ...this.state.electricity,
                    pageRequest: {
                        ...this.state.electricity.pageRequest,
                        page: targetPage - 1,
                    }
                }
            }, () => {
                this.getElectricityInvoices();
            });
        }
    };

    getActiveInvoicesType = async () => {
        DocumentService.getInvoices(this.context.state.customerId, this.state.fuel.pageRequest)
            .then(res => {
                if(!res.content.length){
                    this.setState({
                        ...this.state,
                        activeInvoiceType: ActiveInvoiceType.ELECTRICITY
                    });
                }
            });
    }

    updateCustomerId = (onMount: boolean) => {
        if (this.context.state.customerId !== this.state.customerId ||
            (this.context.state.customer && this.context.state.customer.contactId !== this.state.contactId)
        ) {
            this.setState({
                ...this.state,
                customerId: this.context.state.customerId,
                contactId: (this.context.state.customer && this.context.state.customer.contactId) || this.state.contactId,
                fuel: {
                    ...this.state.fuel,
                    pageRequest: {
                        ...this.state.fuel.pageRequest,
                        page: 0
                    }
                },
                electricity: {
                    ...this.state.electricity,
                    pageRequest: {
                        ...this.state.electricity.pageRequest,
                        page: 0
                    }
                }
            }, () => {                        
                if(onMount){
                    this.getActiveInvoicesType()
                }
                this.getInvoices()
            });
        }
    };

   getInvoices = () => {
        if(this.state.activeInvoiceType === ActiveInvoiceType.FUEL){
            this.getFuelInvoices();
            this.getUnpaidFuelInvoices();
        }else{
            this.getElectricityInvoices();
        }
   }

    onFuelInvoicesSortChange = (sortedByField: IFuelInvoiceSortFields, sortDirection: SortDirection) => {
        this.setState({
            ...this.state,
            fuel: {
                ...this.state.fuel,
                pageRequest: {
                    ...this.state.fuel.pageRequest,
                    sortDirection: sortDirection,
                    sortField: sortedByField
                }
            }
        }, () => {
            this.getFuelInvoices();
        });
    };

    onElectricityInvoicesSortChange = (sortedByField: IElectricityInvoiceSortFields, sortDirection: SortDirection) => {
        this.setState({
            ...this.state,
            electricity: {
                ...this.state.electricity,
                pageRequest: {
                    ...this.state.electricity.pageRequest,
                    sortDirection: sortDirection,
                    sortField: sortedByField
                }
            }
        }, () => {
            this.getElectricityInvoices();
        });
    };

    onFuelInvoicesFilterChange = (type: FuelInvoiceFilterType, value: any) => {
        const pageRequest = {
            ...this.state.fuel.pageRequest,
            page: 0
        };
        pageRequest.types = DOCUMENT_TYPES_MULTI + ',' + DOCUMENT_TYPES_AGGREGATE;
        if (type === FuelInvoiceFilterType.TYPE) {
            if (value.value === FuelInvoiceType.MULTI_BILL) {
                pageRequest.types = DOCUMENT_TYPES_MULTI;
            } else if (value.value === FuelInvoiceType.AGGREGATE_BILL) {
                pageRequest.types = DOCUMENT_TYPES_AGGREGATE;
            }
        } else if (type === FuelInvoiceFilterType.DATE) {
            const val = value as DateRangeInput;
            value.id = val.selectedOptionId;
            pageRequest.documentDateCreatedStart = val.startDate.format('YYYY-MM-DD');
            pageRequest.documentDateCreatedEnd = val.endDate.format('YYYY-MM-DD');
        }
        this.setState({
            ...this.state,
            fuel: {
                ...this.state.fuel,
                filters: {
                    ...this.state.fuel.filters,
                    [type]: value
                },
                pageRequest
            }
        }, () => this.getFuelInvoices());
    }

    clearPaymentStatus = () => {
        this.setState({ 
            ...this.state,
            fuel: {
                ...this.state.fuel,
                paymentStatus: null
            }
         });
    }

    componentDidMount(): void {
        this.updateCustomerId(true);
        const params = getParams(this.props.location.search);
        if (params.payment) {
            this.setState({ 
                ...this.state,
                fuel: {
                    ...this.state.fuel,
                    paymentStatus: params.payment
                }
             });
            this.props.history.replace('/arved');
            if (params.payment === 'success') {
                setTimeout(() => this.clearPaymentStatus(), 5000);
            }
        }
    }

    componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<IInvoicesState>, snapshot?: any): void {
        this.updateCustomerId(false);
    }

    onActiveInvoiceTypeChange = (activeInvoiceType : ActiveInvoiceType): void => {
        this.setState({
            ...this.state,
            activeInvoiceType
        }, () => {
            this.getInvoices()
        })
    }

    render() {
        let payableInvoices: IFuelInvoice[] = [];
        if (this.state.fuel.unpaidInvoices !== null) {
            payableInvoices = this.state.fuel.unpaidInvoices.content.filter(i => i.totalAmount - i.paymentAmount > 0);
        }

        return (
            <div className="invoices__container">
                <InvoicesSearchBar
                    onFuelInvoiceFilterChange={this.onFuelInvoicesFilterChange}
                    fuelInvoicesfilters={this.state.fuel.filters}
                    onActiveInvoicesChange={this.onActiveInvoiceTypeChange}
                    activeInvoices={this.state.activeInvoiceType}
                />
                {this.state.activeInvoiceType === ActiveInvoiceType.FUEL ? (
                    <FuelInvoices 
                        {...this.state.fuel}
                        payableInvoices={payableInvoices}
                        toggleModal={(state) => this.setState({
                            ...this.state,
                            fuel: {
                                ...this.state.fuel,
                                paymentModalVisible: state
                            }
                        })}
                        onPageChange={this.onFuelInvoicePageChange}
                        onSortChange={this.onFuelInvoicesSortChange}
                        beginPayment={this.beginFuelInvoicePayment}
                    />
                ) : (
                    <ElectricityInvoicesTable 
                        {...this.state.electricity}
                        onPageChange={this.onElectricityInvoicePageChange}
                        onSortChange={this.onElectricityInvoicesSortChange}
                    />
                )}            
            </div>
        );
    }
}